import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import { getApiErrorMessage } from '@/api/admin-access';
import {
  applyImportMatch,
  type DataType,
  downloadImportReport,
  downloadUserData,
  executeDataImport,
  getCumulativeImportAiUsage,
  getDataHumidors,
  getDataImport,
  getDataSummary,
  getImportMerchantGroups,
  type ImportHumidor,
  type ImportJob,
  type ImportMode,
  type ImportVenue,
  increaseImportAiBudget,
  listDataImports,
  listImportMatches,
  type MatchGroup,
  type MerchantDecision,
  type MerchantGroup,
  previewDataImport,
  searchImportCigars,
  searchImportVenues,
  skipImportMatch,
  stageDataImport,
} from '@/api/user-data-management';
import type { ManagedUserDetails } from '@/api/users';
import ModalDialog from '@/components/modal-dialog';

import { reviewSearchQuery } from './review-search';

const chooseImportAction = (title: string, message: string, choices: Array<{
  value: string; label: string; color?: string;
}>) => new Promise<string | null>((resolve) => {
  const finish = (choice: string | null) => { ModalDialog.close(); resolve(choice); };
  ModalDialog.show({ title, message, type: 'warning', onClose: () => finish(null),
    buttons: [
      ...choices.map((choice) => ({ label: choice.label, color: choice.color,
        onClick: () => finish(choice.value) })),
      { label: 'Cancel', onClick: () => finish(null) },
    ],
  });
});

const fields: Record<DataType, string[]> = {
  inventory: [
    'source_id',
    'humidor_id',
    'humidor_name',
    'cigar_id',
    'brand',
    'cigar_name',
    'vitola',
    'size',
    'shape',
    'wrapper',
    'variant',
    'quantity',
    'date_added',
    'purchase_date',
    'purchase_price',
    'purchase_location',
    'notes',
  ],
  sessions: [
    'source_id',
    'cigar_id',
    'brand',
    'cigar_name',
    'vitola',
    'size',
    'shape',
    'wrapper',
    'variant',
    'session_date',
    'rating',
    'rating_scale',
    'duration',
    'notes',
    'flavor_notes',
    'strength',
    'humidor_name',
    'buy_again',
    'favorite',
    'top_new_world',
    'top_flavored',
    'purchase_location',
  ],
};
const saveBlob = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
const nameOf = (user: ManagedUserDetails) =>
  user.display_name || user.full_name || `${user.first_name} ${user.last_name}`;
const venueAddress = (venue: ImportVenue) => {
  const cityState = [venue.addr_city, venue.addr_state].filter(Boolean).join(', ');
  const cityLine = [cityState, venue.addr_zip].filter(Boolean).join(' ');
  return [venue.addr_line_1, venue.addr_line_2, cityLine, venue.addr_country]
    .filter(Boolean).join(', ') || venue.addr_formatted || venue.location || 'No address on file';
};
const venueLabel = (venue: ImportVenue) =>
  `${venue.name} #${venue.id} · ${venueAddress(venue)} · ${venue.website?.trim() || 'No website on file'}`;
const statusLabel = (status: string) =>
  ({
    matched: 'Matched Automatically',
    matched_manually: 'Matched Manually',
    needs_review: 'Needs Review',
    not_found: 'Not Found',
    resolved: 'Resolved',
    skipped: 'Skipped',
    imported: 'Imported',
    updated: 'Updated',
    unchanged: 'Unchanged',
    pending_update: 'Queued for update',
    invalid: 'Failed Validation',
    failed: 'Failed',
    awaiting_credits: 'Awaiting AI credits',
    researching: 'Researching cigars',
    staged: 'Queued',
    processing: 'Processing',
    ready_for_review: 'Ready for review',
  })[status] || status;
const csvHeaders = (text: string) => {
  const headers: string[] = [];
  let value = '';
  let quoted = false;
  const input = text.replace(/^\uFEFF/, '');
  for (let index = 0; index < input.length; index++) {
    const character = input[index];
    if (character === '"') {
      if (quoted && input[index + 1] === '"') {
        value += '"';
        index++;
      } else quoted = !quoted;
    } else if (
      !quoted &&
      (character === ',' || character === '\n' || character === '\r')
    ) {
      headers.push(value.trim());
      value = '';
      if (character !== ',') return headers;
    } else value += character;
  }
  headers.push(value.trim());
  return headers;
};
const safeCsvCell = (value: unknown) => {
  let text = String(value ?? '');
  if (/^\s*[=+@\-\t\r]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};

export default function UserDataManagement({
  user,
}: {
  user: ManagedUserDetails;
}) {
  const userId = user.user_id;
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState<Awaited<
    ReturnType<typeof getDataSummary>
  > | null>(null);
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [cumulativeAiUsage, setCumulativeAiUsage] = useState<Awaited<
    ReturnType<typeof getCumulativeImportAiUsage>
  > | null>(null);
  const [type, setType] = useState<DataType>('inventory');
  const [importMode, setImportMode] = useState<ImportMode>('create');
  const [useAi, setUseAi] = useState(true);
  const [aiCreditBudget, setAiCreditBudget] = useState(4000);
  const [newAiBudget, setNewAiBudget] = useState('');
  const [creditClassification, setCreditClassification] = useState<
    'customer' | 'promotional'
  >('customer');
  const [humidors, setHumidors] = useState<ImportHumidor[]>([]);
  const [destinationHumidor, setDestinationHumidor] = useState('default');
  const [file, setFile] = useState<{ name: string; csv: string } | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<any>(null);
  const [merchantGroups, setMerchantGroups] = useState<MerchantGroup[] | null>(null);
  const [merchantMappings, setMerchantMappings] = useState<Record<string, MerchantDecision>>({});
  const [venueQuery, setVenueQuery] = useState<Record<string, string>>({});
  const [venueCity, setVenueCity] = useState<Record<string, string>>({});
  const [venueWebsite, setVenueWebsite] = useState<Record<string, string>>({});
  const [venueResults, setVenueResults] = useState<Record<string, ImportVenue[]>>({});
  const [venuePages, setVenuePages] = useState<Record<string, number>>({});
  const [selectedVenues, setSelectedVenues] = useState<Record<string, ImportVenue>>({});
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobDetail, setJobDetail] = useState<Awaited<
    ReturnType<typeof getDataImport>
  > | null>(null);
  const [groups, setGroups] = useState<MatchGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<MatchGroup | null>(null);
  const [reviewCigarId, setReviewCigarId] = useState<number | null>(null);
  const [reviewVitolaChoice, setReviewVitolaChoice] = useState<number | 'unlinked' | null>(null);
  const [vitolaFilter, setVitolaFilter] = useState('');
  const [query, setQuery] = useState('');
  const [matchSearching, setMatchSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const matchSearchSequence = useRef(0);
  const [cigars, setCigars] = useState<
    Awaited<ReturnType<typeof searchImportCigars>>
  >([]);
  const [exportInventory, setExportInventory] = useState(true);
  const [exportSessions, setExportSessions] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeModal, setActiveModal] = useState<'import' | 'export' | null>(
    null
  );
  const [error, setError] = useState('');
  const totalImportRows = jobDetail?.progress.total || 0;
  const processedImportRows = jobDetail?.progress.finished || 0;

  const refresh = useCallback(async () => {
    const [nextSummary, nextJobs, nextAiUsage] = await Promise.all([
      getDataSummary(userId),
      listDataImports(userId),
      getCumulativeImportAiUsage(userId),
    ]);
    setSummary(nextSummary);
    setJobs(nextJobs);
    setCumulativeAiUsage(nextAiUsage);
  }, [userId]);
  useEffect(() => {
    refresh().catch((err) => setError(getApiErrorMessage(err)));
  }, [refresh]);
  useEffect(() => {
    if (activeModal !== 'import' || type !== 'inventory' || jobId) return;
    getDataHumidors(userId)
      .then((items) => {
        setHumidors(items);
      })
      .catch((err) => setError(getApiErrorMessage(err)));
  }, [activeModal, type, userId, jobId]);
  const loadJob = async (id: string) => {
    setActiveModal('import');
    setJobId(id);
    const [detail, matches] = await Promise.all([
      getDataImport(userId, id),
      listImportMatches(userId, id),
    ]);
    setJobDetail(detail);
    setGroups(matches);
  };
  useEffect(() => {
    const linkedJob = new URLSearchParams(location.search).get('importJobId');
    if (!linkedJob) return;
    getDataImport(userId, linkedJob)
      .then((detail) => {
        setJobId(linkedJob);
        setJobDetail(detail);
        setActiveModal('import');
        return listImportMatches(userId, linkedJob);
      })
      .then(setGroups)
      .catch((err) => setError(getApiErrorMessage(err)));
  }, [location.search, userId]);
  useEffect(() => {
    if (
      activeModal !== 'import' ||
      !jobId ||
      !['staged', 'processing', 'researching'].includes(
        jobDetail?.job.status || ''
      )
    )
      return undefined;
    const timer = window.setInterval(() => {
      getDataImport(userId, jobId)
        .then((detail) => {
          setJobDetail(detail);
          if (
            !['staged', 'processing', 'researching'].includes(detail.job.status)
          ) {
            listImportMatches(userId, jobId)
              .then(setGroups)
              .catch(() => {});
            refresh().catch(() => {});
          }
        })
        .catch((err) => setError(getApiErrorMessage(err)));
    }, 5000);
    return () => window.clearInterval(timer);
  }, [activeModal, jobId, jobDetail?.job.status, userId, refresh]);
  const onFile = async (chosen?: File) => {
    setPreview(null);
    setMerchantGroups(null);
    setMerchantMappings({});
    setJobId(null);
    setJobDetail(null);
    setError('');
    if (!chosen) {
      setFile(null);
      return;
    }
    if (
      !chosen.name.toLowerCase().endsWith('.csv') ||
      chosen.size > 8 * 1024 * 1024
    ) {
      setError('Select a CSV file under 8 MB.');
      return;
    }
    try {
      const text = new TextDecoder('utf-8', { fatal: true }).decode(
        await chosen.arrayBuffer()
      );
      if (text.includes('\0'))
        throw new Error('The CSV contains invalid text.');
      setFile({ name: chosen.name, csv: text });
      const guessed = csvHeaders(text);
      if (!guessed.includes('boxpressd__import_row_id') && chosen.size > 2 * 1024 * 1024)
        throw new Error('New imports must be under 2 MB. Reimport CSVs may be up to 8 MB.');
      setHeaders(guessed);
      setImportMode(guessed.includes('boxpressd__import_row_id') ? 'update' : 'create');
      setMapping(
        Object.fromEntries(
          guessed
            .filter((h) => fields[type].includes(h.toLowerCase()) ||
              (type === 'sessions' && ['smoke_date', 'topw', 'topf'].includes(h.toLowerCase())))
            .map((h) => [h, type === 'sessions'
              ? ({ smoke_date: 'session_date', topw: 'top_new_world', topf: 'top_flavored' } as Record<string, string>)[h.toLowerCase()] || h.toLowerCase()
              : h.toLowerCase()])
        )
      );
    } catch (err) {
      setError((err as Error).message || 'Could not read the CSV as UTF-8.');
    }
  };
  const run = async (task: () => Promise<void>) => {
    setBusy(true);
    setError('');
    try {
      await task();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const searchReviewCigars = async (text: string, suggestedCigarId?: number | null) => {
    const nextQuery = text.trim();
    if (nextQuery.length < 2) return;
    const sequence = ++matchSearchSequence.current;
    setMatchSearching(true);
    setHasSearched(false);
    setReviewCigarId(null);
    setReviewVitolaChoice(null);
    setVitolaFilter('');
    setError('');
    try {
      const results = await searchImportCigars(userId, nextQuery);
      if (sequence === matchSearchSequence.current) {
        setCigars([...results].sort((left, right) =>
          Number(right.cigar_id === suggestedCigarId) -
          Number(left.cigar_id === suggestedCigarId)));
        // Open the suggested cigar's vitolas for inspection. This does not
        // select a match or submit anything to the import job.
        setReviewCigarId(results.some((item) => item.cigar_id === suggestedCigarId)
          ? suggestedCigarId! : null);
        setHasSearched(true);
      }
    } catch (err) {
      if (sequence === matchSearchSequence.current) setError(getApiErrorMessage(err));
    } finally {
      if (sequence === matchSearchSequence.current) setMatchSearching(false);
    }
  };
  const closeReviewMatch = () => {
    matchSearchSequence.current++;
    setSelectedGroup(null);
    setMatchSearching(false);
    setCigars([]);
    setReviewCigarId(null);
    setReviewVitolaChoice(null);
    setVitolaFilter('');
  };
  const closeImportModal = () => {
    closeReviewMatch();
    setActiveModal(null);
  };
  const openReviewMatch = (group: MatchGroup) => {
    const initialQuery = reviewSearchQuery(group);
    setSelectedGroup(group);
    setQuery(initialQuery);
    setCigars([]);
    setHasSearched(false);
    setReviewCigarId(null);
    setReviewVitolaChoice(null);
    setVitolaFilter('');
    if (initialQuery.length >= 2)
      searchReviewCigars(initialQuery, group.suggested_cigar_id);
  };
  let selectedDestination:
    | { mode: 'default' }
    | { mode: 'new' }
    | { mode: 'existing'; humidorId: number };
  if (destinationHumidor === 'default') {
    selectedDestination = { mode: 'default' };
  } else if (destinationHumidor === 'new') {
    selectedDestination = { mode: 'new' };
  } else {
    selectedDestination = {
      mode: 'existing',
      humidorId: Number(destinationHumidor),
    };
  }
  const payload = file && {
    dataType: type,
    filename: file.name,
    csv: file.csv,
    mapping,
    importMode,
    use_ai: useAi,
    aiCreditBudget,
    merchantMappings,
    creditClassification,
    ...(type === 'inventory' ? { destination: selectedDestination } : {}),
  };
  const merchantStatus = (group: MerchantGroup) => {
    const decision = merchantMappings[group.key];
    if (decision?.decision === 'venue') {
      const selected = selectedVenues[group.key];
      return `Matched: ${selected ? venueLabel(selected) : `Venue #${decision.venueId}`}`;
    }
    if (decision?.decision === 'unlinked') return 'Leave unlinked';
    if (group.lookup.status === 'needs_review') return 'Needs selection';
    return 'Suggested — confirm';
  };
  const count = (status: string) =>
    jobDetail?.counts.find((item) => item.status === status)?.count || 0;
  const purchaseVenueLabel = (row: any) => {
    if (row.purchaseVenue?.venueId) {
      return (
        <span
          title={`${row.data.purchase_location} → ${row.purchaseVenue.venueName} (${row.purchaseVenue.method})`}
          style={{ cursor: 'help', textDecoration: 'underline dotted' }}
        >
          {row.purchaseVenue.venueName} #{row.purchaseVenue.venueId}
        </span>
      );
    }
    if (row.data.purchase_location) {
      return (
        <span title="Original merchant text will be preserved without a venue link">
          Text only ({row.purchaseVenue?.status || 'unlinked'})
        </span>
      );
    }
    return '—';
  };

  return (
    <>
      <Card style={{ marginTop: 16, padding: 20 }}>
        <h5>Data Management</h5>
        <p>
          <strong>Target account:</strong> {nameOf(user)} · {user.email} · ID{' '}
          {userId} · {user.active ? 'Active' : 'Inactive'}
        </p>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <h6>Data summary</h6>
        {summary ? (
          <p>
            {summary.humidors} humidors · {summary.inventory_records} inventory
            records · {summary.total_quantity} cigars · {summary.sessions} smoke
            sessions
          </p>
        ) : (
          <p>Loading summary…</p>
        )}
        <hr />
        <h6>AI costs</h6>
        <p className="mb-1">
          {cumulativeAiUsage?.requests || 0} requests · $
          {Number(cumulativeAiUsage?.total_cost_usd || 0).toFixed(4)}
        </p>
        <small className="text-muted">
          AI credits consumed: {Number(cumulativeAiUsage?.credits_charged || 0)}
        </small>
        {cumulativeAiUsage?.estimated_requests ? (
          <div>
            <small className="text-muted">Includes estimated costs</small>
          </div>
        ) : null}
        <hr />
        <h6>Import history</h6>
        {jobs.length ? (
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {jobs.map((job) => (
              <div key={job.id} className="border-bottom py-2">
                <div
                  className="font-weight-bold"
                  style={{ overflowWrap: 'anywhere' }}
                >
                  {job.filename}
                </div>
                <small className="text-muted">
                  {new Date(job.created_at).toLocaleString()} · {job.data_type}{' '}
                  · {statusLabel(job.status)}
                </small>
                <div>
                  <small>
                    {job.unresolved || 0} unresolved · AI $
                    {Number(job.ai_usage?.total_cost_usd || 0).toFixed(4)}
                  </small>
                </div>
                <Button
                  size="sm"
                  color="link"
                  className="p-0"
                  onClick={() => run(() => loadJob(job.id))}
                >
                  Review matches / report
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted">No imports yet.</p>
        )}
        <div className="d-flex mt-3" style={{ gap: 8 }}>
          <Button
            color="primary"
            onClick={() => {
              setError('');
              setJobId(null);
              setJobDetail(null);
              setActiveModal('import');
            }}
          >
            Import data
          </Button>
          <Button
            color="secondary"
            onClick={() => {
              setError('');
              setActiveModal('export');
            }}
          >
            Export data
          </Button>
        </div>
      </Card>
      <Modal
        isOpen={activeModal === 'import'}
        toggle={() => !busy && closeImportModal()}
        size="xl"
        scrollable
      >
        <ModalHeader toggle={() => !busy && closeImportModal()}>
          Import data for {nameOf(user)}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            {user.email} · User ID {userId}
          </p>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <h6>Import data</h6>
          <p>
            CSV only. Upload inventory and smoke sessions as separate files.
            Unmatched cigars remain pending until reviewed.
          </p>
          <div className="form-group mb-3">
            <label htmlFor="data-type">Data type</label>
            <select
              id="data-type"
              className="form-control"
              value={type}
              onChange={(event) => {
                setType(event.target.value as DataType);
                setFile(null);
                setPreview(null);
                setMerchantGroups(null);
                setMerchantMappings({});
              }}
            >
              <option value="inventory">Humidor inventory</option>
              <option value="sessions">Smoke sessions / journal</option>
            </select>
          </div>
          {type === 'inventory' && !jobId && (
            <div className="form-group mb-3">
              <label htmlFor="import-humidor">Destination humidor</label>
              <select
                id="import-humidor"
                className="form-control"
                value={destinationHumidor}
                onChange={(event) => setDestinationHumidor(event.target.value)}
              >
                <option value="default">
                  Default humidor
                  {humidors.find((humidor) => humidor.is_default)?.name
                    ? ` (${humidors.find((humidor) => humidor.is_default)?.name})`
                    : ''}
                </option>
                {humidors.map((humidor) => (
                  <option key={humidor.id} value={humidor.id}>
                    {humidor.name}
                    {humidor.is_default ? ' (default)' : ''}
                  </option>
                ))}
                <option value="new">
                  Create New Humidor (Imported Humidor)
                </option>
              </select>
            </div>
          )}
          <div className="form-group mb-3">
            <label htmlFor="import-mode">Import mode</label>
            <select id="import-mode" className="form-control" value={importMode}
              onChange={(event) => { setImportMode(event.target.value as ImportMode); setPreview(null); }}>
              <option value="create">Create</option>
              <option value="update">Update existing records by Boxpressd ID</option>
              <option value="upsert">Update, plus explicitly eligible new rows</option>
            </select>
            {importMode !== 'create' && <small>
              Use a downloaded reimport CSV. Blank fields keep their current values.
              Set boxpressd__clear_fields to a comma-separated optional-field list to clear values.
              To correct a cigar or vitola, edit its boxpressd__ ID column.
            </small>}
          </div>
          <div className="form-group mb-3">
            <div className="form-check">
              <input id="import-use-ai" type="checkbox" className="form-check-input"
                checked={useAi} onChange={(event) => setUseAi(event.target.checked)} />
              <label className="form-check-label" htmlFor="import-use-ai">Use AI Matching</label>
            </div>
            <small>Use AI to help resolve cigars that cannot be matched automatically. Disabling this option imports all deterministically matched records and saves unresolved records for later review.</small>
            {useAi && <div className="mt-2">
              <label htmlFor="import-ai-credit-budget">Maximum AI credits for this job</label>
              <input id="import-ai-credit-budget" className="form-control" type="number"
                min={2000} max={2000000} step={1} value={aiCreditBudget}
                onChange={(event) => setAiCreditBudget(Number(event.target.value))} />
              <small>Processing pauses before another AI operation would exceed this limit. Credits settle against measured usage.</small>
            </div>}
          </div>
          <div className="form-group mb-3">
            <label htmlFor="data-file">Source CSV</label>
            <input
              id="data-file"
              type="file"
              accept=".csv,text/csv"
              className="form-control"
              onChange={(event) => onFile(event.target.files?.[0])}
            />
          </div>
          <div className="form-group mb-3">
            <label htmlFor="import-credit-classification">
              AI credit source
            </label>
            <select
              id="import-credit-classification"
              className="form-control"
              value={creditClassification}
              onChange={(event) =>
                setCreditClassification(
                  event.target.value as 'customer' | 'promotional'
                )
              }
            >
              <option value="customer">Customer credits</option>
              <option value="promotional">
                Complimentary promotional credits
              </option>
            </select>
            <small>
              Only AI research consumes credits. Confirm that complimentary
              credits have been allocated before selecting promotional.
            </small>
          </div>
          {file && (
            <>
              <h6>Field mapping</h6>
              <p>
                Map CSV columns to Boxpressd fields. Unmapped columns remain in
                the saved source record.
              </p>
              {headers.map((header) => (
                <div key={header} className="row mb-2">
                  <label className="col-sm-5" htmlFor={`map-${header}`}>
                    {header}
                  </label>
                  <div className="col-sm-7">
                    <select
                      id={`map-${header}`}
                      className="form-control"
                      value={mapping[header] || ''}
                      onChange={(event) => {
                        setMapping((current) => ({
                          ...current,
                          [header]: event.target.value,
                        }));
                        setPreview(null);
                        setMerchantGroups(null);
                        setMerchantMappings({});
                      }}
                    >
                      <option value="">Do not map</option>
                      {fields[type].map((field) => (
                        <option value={field} key={field}>
                          {field.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
              {importMode === 'create' && type === 'inventory' && Object.values(mapping).includes('purchase_location') && (
                <div className="mt-3 mb-3">
                  <h6>Purchase location mapping</h6>
                  <p>Review each distinct CSV merchant. Confirm a venue or leave the location unlinked.</p>
                  <Button color="secondary" disabled={busy} onClick={() => run(async () => {
                    const found = await getImportMerchantGroups(userId, payload!);
                    setMerchantGroups(found);
                    setVenueResults(Object.fromEntries(found.map((group) => [group.key, group.candidates])));
                    setVenueQuery(Object.fromEntries(found.map((group) => [group.key,
                      group.lookup.suggestedName || group.sourceValue])));
                    setVenueCity({});
                    setVenueWebsite({});
                    setVenuePages({});
                    setMerchantMappings({});
                    setSelectedVenues({});
                    setPreview(null);
                  })}>Find purchase locations</Button>
                  {merchantGroups?.map((group) => (
                    <div key={group.key} className="border rounded p-2 mt-2">
                      <strong>{group.sourceValue}</strong> · {group.count} rows<br />
                      <small>{group.lookup.suggestedName ? `Suggested: ${group.lookup.suggestedName}. ` : ''}
                        {group.lookup.reason || group.lookup.note || group.lookup.status}</small><br />
                      {group.candidate?.venueId && group.lookup.status !== 'needs_review' && (
                        <Button size="sm" color="secondary" className="mr-2" onClick={() => {
                          setPreview(null);
                          const selected = group.candidates.find((venue) => venue.id === group.candidate!.venueId);
                          if (selected) setSelectedVenues((current) => ({ ...current, [group.key]: selected }));
                          setMerchantMappings((current) => ({ ...current,
                            [group.key]: { decision: 'venue', venueId: group.candidate!.venueId! } }));
                        }}>Confirm {group.candidates.find((venue) => venue.id === group.candidate!.venueId)
                          ? venueLabel(group.candidates.find((venue) => venue.id === group.candidate!.venueId)!)
                          : `${group.candidate.venueName} #${group.candidate.venueId}`}</Button>
                      )}
                      <div className="row mt-2">
                        <div className="col-md-4 mb-2">
                          <label htmlFor={`venue-name-${group.key}`}>Venue name</label>
                          <input id={`venue-name-${group.key}`} className="form-control"
                            placeholder="Name" value={venueQuery[group.key] || ''}
                            onChange={(event) => {
                              setVenueQuery((current) => ({ ...current, [group.key]: event.target.value }));
                              setVenuePages((current) => ({ ...current, [group.key]: 0 }));
                              setVenueResults((current) => ({ ...current, [group.key]: [] }));
                            }} />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label htmlFor={`venue-city-${group.key}`}>City (optional)</label>
                          <input id={`venue-city-${group.key}`} className="form-control"
                            placeholder="City" value={venueCity[group.key] || ''}
                            onChange={(event) => {
                              setVenueCity((current) => ({ ...current, [group.key]: event.target.value }));
                              setVenuePages((current) => ({ ...current, [group.key]: 0 }));
                              setVenueResults((current) => ({ ...current, [group.key]: [] }));
                            }} />
                        </div>
                        <div className="col-md-4 mb-2">
                          <label htmlFor={`venue-website-${group.key}`}>Website (optional)</label>
                          <input id={`venue-website-${group.key}`} className="form-control"
                            placeholder="Domain or website" value={venueWebsite[group.key] || ''}
                            onChange={(event) => {
                              setVenueWebsite((current) => ({ ...current, [group.key]: event.target.value }));
                              setVenuePages((current) => ({ ...current, [group.key]: 0 }));
                              setVenueResults((current) => ({ ...current, [group.key]: [] }));
                            }} />
                        </div>
                      </div>
                      <Button size="sm" color="secondary" className="mt-1" disabled={busy || (venueQuery[group.key] || '').length < 2}
                        onClick={() => run(async () => {
                          const results = await searchImportVenues(userId, venueQuery[group.key], 0,
                            { city: venueCity[group.key], website: venueWebsite[group.key] });
                          setVenueResults((current) => ({ ...current, [group.key]: results }));
                          setVenuePages((current) => ({ ...current, [group.key]: 0 }));
                        })}>Search</Button>
                      {(venueResults[group.key] || []).map((venue) => (
                        <div key={venue.id}>
                          <Button size="sm" color="link" onClick={() => {
                            setPreview(null);
                            setSelectedVenues((current) => ({ ...current, [group.key]: venue }));
                            setMerchantMappings((current) => ({ ...current,
                              [group.key]: { decision: 'venue', venueId: venue.id } }));
                          }}>Select {venueLabel(venue)}</Button>
                        </div>
                      ))}
                      {(venuePages[group.key] || 0) > 0 && <Button size="sm" color="link" onClick={() => run(async () => {
                        const nextPage = (venuePages[group.key] || 0) - 1;
                        const results = await searchImportVenues(userId, venueQuery[group.key], nextPage,
                          { city: venueCity[group.key], website: venueWebsite[group.key] });
                        setVenueResults((current) => ({ ...current, [group.key]: results }));
                        setVenuePages((current) => ({ ...current, [group.key]: nextPage }));
                      })}>Previous venues</Button>}
                      {(venueResults[group.key] || []).length === 20 && <Button size="sm" color="link" onClick={() => run(async () => {
                        const nextPage = (venuePages[group.key] || 0) + 1;
                        const results = await searchImportVenues(userId, venueQuery[group.key], nextPage,
                          { city: venueCity[group.key], website: venueWebsite[group.key] });
                        setVenueResults((current) => ({ ...current, [group.key]: results }));
                        setVenuePages((current) => ({ ...current, [group.key]: nextPage }));
                      })}>More venues</Button>}
                      <Button size="sm" color="link" onClick={() => {
                        setPreview(null);
                        setSelectedVenues((current) => { const copy = { ...current }; delete copy[group.key]; return copy; });
                        setMerchantMappings((current) => ({ ...current, [group.key]: { decision: 'unlinked' } }));
                      }}>Leave unlinked</Button>
                      <div><small>Status: {merchantStatus(group)}</small></div>
                    </div>
                  ))}
                </div>
              )}
              <Button color="secondary" disabled={busy || (importMode === 'create' && type === 'inventory' && Object.values(mapping).includes('purchase_location') &&
                (merchantGroups === null || merchantGroups.some((group) => !merchantMappings[group.key])))}
                onClick={() => run(async () => { setPreview(await previewDataImport(userId, payload!)); })}>
                Preview / dry run
              </Button>
            </>
          )}
          {preview && (
            <div className="mt-3">
              <h6>Dry run</h6>
              {importMode === 'create' ? <p>
                {preview.total} source records · {preview.valid} valid ·{' '}
                {preview.invalid} invalid ·{' '}
                {preview.matchesAreSample ? 'First 30 rows: ' : ''}
                {preview.matches?.matched || 0} matched ·{' '}
                {preview.matches?.needs_review || 0} needs review ·{' '}
                {preview.matches?.not_found || 0} not found
              </p> : <p>
                {preview.total} rows · {preview.recordsToCreate || 0} to create ·{' '}
                {preview.recordsToUpdate || 0} to update ·{' '}
                {preview.recordsUnchanged || 0} unchanged ·{' '}
                {preview.recordsRequiringReview || 0} may need review ·{' '}
                {preview.recordsRejected || 0} rejected
              </p>}
              {importMode === 'create' && <p>
                {preview.matchesAreSample ? 'Sample' : 'Estimated'} records to
                create: {preview.recordsToCreate || 0} · to update: 0 · to skip:{' '}
                {preview.recordsToSkip || 0}. Existing exported records may be
                skipped during execution.
              </p>}
              {importMode !== 'create' ? (
                <table className="table table-sm"><thead><tr>
                  <th>Row</th><th>Action</th><th>Target record ID</th><th>Changed fields</th><th>Errors</th>
                </tr></thead><tbody>{preview.preview.map((row: any) => (
                  <tr key={row.row}><td>{row.row}</td><td>{row.action}</td>
                    <td>{row.targetRecordId || '—'}</td>
                    <td>{row.changedFields?.join(', ') || '—'}</td>
                    <td>{row.errors?.join('; ') || ''}</td></tr>
                ))}</tbody></table>
              ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Cigar</th>
                      <th>Quantity / rating</th>
                      <th>Match</th>
                      <th>Suggested cigar ID</th>
                      <th>Suggested vitola ID</th>
                      <th>Cigar confidence</th>
                      <th>Vitola confidence</th>
                      <th>Catalog evidence</th>
                      {type === 'inventory' && <th>Purchase venue</th>}
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((row: any) => (
                      <tr key={row.row}>
                        <td>{row.row}</td>
                        <td>
                          {row.data.brand} {row.data.cigar_name}
                        </td>
                        <td>
                          {row.data.quantity ??
                            row.data.normalized_rating ??
                            ''}
                        </td>
                        <td>{row.cigarMatch?.status || ''}</td>
                        <td>
                          {row.cigarMatch?.suggestedId ? (
                            <span
                              title={
                                row.cigarMatch.suggestedCigarFullName || ''
                              }
                              style={{
                                cursor: 'help',
                                textDecoration: 'underline dotted',
                              }}
                            >
                              {row.cigarMatch.suggestedId}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {row.cigarMatch?.vitolaId ? (
                            <span
                              title={
                                row.cigarMatch.suggestedVitolaFormattedName ||
                                ''
                              }
                              style={{
                                cursor: 'help',
                                textDecoration: 'underline dotted',
                              }}
                            >
                              {row.cigarMatch.vitolaId}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {row.cigarMatch?.confidence == null
                            ? '—'
                            : `${Math.round(row.cigarMatch.confidence * 100)}%`}
                        </td>
                        <td>
                          {row.cigarMatch?.vitolaConfidence == null
                            ? 'Unverified'
                            : `${Math.round(row.cigarMatch.vitolaConfidence * 100)}%`}
                        </td>
                        <td>
                          {row.cigarMatch?.matchSource ||
                            row.cigarMatch?.reviewReason ||
                            '—'}
                          {row.cigarMatch?.candidateNames?.length ? (
                            <span>
                              {' · Candidate IDs: '}
                              {row.cigarMatch.candidateNames.map(
                                (candidate: any, index: number) => (
                                  <React.Fragment key={candidate.id}>
                                    {index > 0 ? ', ' : ''}
                                    <span
                                      title={candidate.fullName}
                                      style={{
                                        cursor: 'help',
                                        textDecoration: 'underline dotted',
                                      }}
                                    >
                                      {candidate.id}
                                    </span>
                                  </React.Fragment>
                                )
                              )}
                            </span>
                          ) : (
                            ''
                          )}
                        </td>
                        {type === 'inventory' && (
                          <td>{purchaseVenueLabel(row)}</td>
                        )}
                        <td>{row.errors.join('; ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
              {preview.exceptions?.length > 0 && (
                <Button
                  color="secondary"
                  className="mr-2"
                  onClick={() => {
                    const reportColumns = [
                      'row',
                      'brand',
                      'cigar_name',
                      'errors',
                    ];
                    const lines = preview.exceptions.map(
                      (entry: Record<string, string>) =>
                        reportColumns
                          .map((header) => safeCsvCell(entry[header]))
                          .join(',')
                    );
                    saveBlob(
                      new Blob(
                        [
                          `\uFEFF${reportColumns.join(',')}\r\n${lines.join('\r\n')}`,
                        ],
                        { type: 'text/csv;charset=utf-8' }
                      ),
                      `boxpressd-import-exceptions-${userId}.csv`
                    );
                  }}
                >
                  Download exception report
                </Button>
              )}
              <Button
                color="primary"
                disabled={busy || !preview.valid}
                onClick={() =>
                  run(async () => {
                    const confirmed = await chooseImportAction('Confirm import',
                      importMode === 'create'
                        ? `Start importing high-confidence matches into ${nameOf(user)} (${user.email}, ID ${userId})?`
                        : `Apply ${preview.recordsToUpdate || 0} updates and ${preview.recordsToCreate || 0} creates to ${nameOf(user)} (${user.email}, ID ${userId})?`,
                      [{ value: 'confirm', label: 'Start import', color: 'primary' }]);
                    if (confirmed !== 'confirm') return;
                    const settings = {
                      ...payload!,
                      ...(importMode !== 'create' ? { confirmUpdates: true, confirmTargetUserId: userId } : {}),
                    };
                    let staged;
                    try {
                      staged = await stageDataImport(userId, settings);
                    } catch (err) {
                      const data = (err as { response?: { data?: any } })?.response?.data;
                      if (data?.code !== 'import_already_exists' || !data.previous || importMode !== 'create') throw err;
                      const previous = data.previous;
                      const choice = await chooseImportAction('This file was imported before',
                        `Previous job ${previous.jobId}: ${previous.imported} of ${previous.total} rows imported; ${previous.remaining} did not import. A new attempt skips previously imported rows. Use Update mode to change those records. Unresolved rows may require new AI credits.`,
                        [
                          { value: 'open', label: 'Open previous import', color: 'secondary' },
                          ...(previous.retryAllowed ? [{ value: 'retry', label: 'Retry remaining rows', color: 'warning' }] : []),
                        ]);
                      if (choice === 'open') { await loadJob(previous.jobId); return; }
                      if (choice !== 'retry') return;
                      const retryTokenKey = `admin-import-retry:${userId}:${previous.jobId}:${useAi ? 'ai' : 'deterministic'}:${aiCreditBudget}`;
                      const attemptKey = sessionStorage.getItem(retryTokenKey) || crypto.randomUUID();
                      sessionStorage.setItem(retryTokenKey, attemptKey);
                      staged = await stageDataImport(userId, { ...settings,
                        use_ai: useAi,
                        retryOfJobId: previous.jobId, attemptKey });
                      sessionStorage.removeItem(retryTokenKey);
                    }
                    await loadJob(staged.jobId);
                    await refresh();
                  })
                }
              >
                Start import for {nameOf(user)}
              </Button>
            </div>
          )}
          {jobDetail && (
            <div className="mt-4">
              <h6>Import {jobDetail.job.id}</h6>
              {['staged', 'processing', 'researching'].includes(
                jobDetail.job.status
              ) && (
                <div className="mb-3">
                  <div
                    className="progress"
                    role="progressbar"
                    aria-label="Import progress"
                    aria-valuenow={processedImportRows}
                    aria-valuemin={0}
                    aria-valuemax={totalImportRows}
                  >
                    <div
                      className="progress-bar"
                      style={{
                        width: `${Math.round((100 * processedImportRows) / Math.max(1, totalImportRows))}%`,
                      }}
                    />
                  </div>
                  <small>
                    {processedImportRows} of {totalImportRows} rows processed.
                    You can leave this page.
                  </small>
                </div>
              )}
              <p>
                {jobDetail.counts.filter((c) => ['imported', 'resolved'].includes(c.status))
                  .reduce((sum, c) => sum + Number(c.count), 0)} successfully imported ·{' '}
                {jobDetail.progress.deterministic_imported} matched automatically ·{' '}
                {jobDetail.counts.find((c) => c.status === 'needs_review')?.count || 0} needs review ·{' '}
                {jobDetail.counts.find((c) => c.status === 'not_found')?.count || 0} not found ·{' '}
                {jobDetail.progress.ai_skipped || 0} skipped AI matching
              </p>
              <p>AI matching: {jobDetail.job.use_ai === false ? 'Off' : 'On'} ·{' '}
                {jobDetail.progress.ai_imported} imported using AI ·{' '}
                {jobDetail.progress.research_pending} researching ·{' '}
                {jobDetail.progress.awaiting_credits} awaiting credits
              </p>
              {jobDetail.job.use_ai !== false && <p>
                Job AI limit: {Number(jobDetail.job.ai_credit_budget || 0).toLocaleString()} credits.
              </p>}
              {jobDetail.job.use_ai !== false &&
                ['ready_for_review', 'partial', 'completed'].includes(jobDetail.job.status) &&
                jobDetail.progress.awaiting_credits > 0 && <div className="form-group mb-3">
                  <label htmlFor="increase-import-ai-budget">New total AI credit limit</label>
                  <div className="d-flex align-items-center">
                    <input id="increase-import-ai-budget" className="form-control mr-2"
                      type="number" min={Number(jobDetail.job.ai_credit_budget || 0) + 1}
                      max={2000000} step={1} value={newAiBudget}
                      onChange={(event) => setNewAiBudget(event.target.value)} />
                    <Button color="warning" disabled={busy || !Number(newAiBudget)}
                      onClick={() => run(async () => {
                        const newLimit = Number(newAiBudget);
                        const choice = await chooseImportAction('Authorize more AI credits',
                          `Increase this job's total AI spending limit to ${newLimit.toLocaleString()} credits for ${nameOf(user)}? This does not add credits to the account.`,
                          [{ value: 'confirm', label: 'Authorize and resume', color: 'warning' }]);
                        if (choice !== 'confirm') return;
                        await increaseImportAiBudget(userId, jobDetail.job.id, newLimit);
                        setNewAiBudget('');
                        await loadJob(jobDetail.job.id);
                      })}>Authorize and resume</Button>
                  </div>
                  <small>Only rows paused by this job's limit resume. Account credits are still required.</small>
                </div>}
              <p>
                Status: {statusLabel(jobDetail.job.status)} ·{' '}
                {jobDetail.counts
                  .map((c) => `${statusLabel(c.status)}: ${c.count}`)
                  .join(' · ')}
                {jobDetail.job.completed_at &&
                  ` · Completed ${new Date(jobDetail.job.completed_at).toLocaleString()}`}
              </p>
              <div className="alert alert-light">
                <strong>AI Usage Summary</strong>
                <div>AI Requests: {jobDetail.ai_usage.requests}</div>
                <div>
                  Input Tokens:{' '}
                  {Number(jobDetail.ai_usage.input_tokens).toLocaleString()}
                </div>
                <div>
                  Output Tokens:{' '}
                  {Number(jobDetail.ai_usage.output_tokens).toLocaleString()}
                </div>
                <div>
                  Cached Input Tokens:{' '}
                  {Number(
                    jobDetail.ai_usage.cached_input_tokens
                  ).toLocaleString()}
                </div>
                <div>
                  Total AI Cost: $
                  {Number(jobDetail.ai_usage.total_cost_usd).toFixed(4)}
                  {jobDetail.ai_usage.estimated_requests
                    ? ' (includes estimates)'
                    : ''}
                </div>
                <div>
                  Billing: {jobDetail.ai_usage.billing_mode === 'user_credits'
                    ? 'Target user credits'
                    : jobDetail.ai_usage.billing_mode === 'mixed'
                      ? 'Mixed'
                      : 'Boxpressd'} · User Credits Charged:{' '}
                  {Number(jobDetail.ai_usage.credits_charged || 0).toLocaleString()}
                </div>
                {jobDetail.ai_usage.breakdown?.map((item) => (
                  <div key={`${item.operation}-${item.provider}-${item.model}`}>
                    {item.operation.replace(/_/g, ' ')} · {item.provider} /{' '}
                    {item.model}: {item.requests} requests, $
                    {Number(item.total_cost_usd).toFixed(4)}
                    {item.estimated_requests ? ' estimated in part' : ''}
                  </div>
                ))}
              </div>
              {jobDetail.job.completed_at && (
                <div className="alert alert-info">
                  <strong>{count('awaiting_credits') || count('needs_review') ||
                    count('not_found') || count('failed')
                    ? 'Processing finished — records need attention'
                    : 'Import complete'}</strong>
                  <br />
                  Successfully imported: {count('imported') + count('resolved')}
                  <br />
                  Requires cigar matching:{' '}
                  {count('needs_review') +
                    count('not_found') +
                    count('skipped')}
                  <br />Awaiting AI credits or budget: {count('awaiting_credits')}
                  <br />
                  Failed validation: {count('invalid')}
                  {jobDetail.job.import_mode && jobDetail.job.import_mode !== 'create' && <>
                    <br />Updated: {count('updated')} · Unchanged: {count('unchanged')}
                  </>}
                </div>
              )}
              <p>
                Review the staged rows before writing data to {nameOf(user)}.
              </p>
              <div style={{ maxHeight: 250, overflow: 'auto' }}>
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Cigar</th>
                      <th>Status</th>
                      <th>Record ID</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobDetail.rows.map((row) => (
                      <tr key={row.source_key}>
                        <td>{row.row_number}</td>
                        <td>
                          {row.normalized.brand} {row.normalized.cigar_name}
                        </td>
                        <td>{statusLabel(row.status)}</td>
                        <td>{row.record_id || ''}</td>
                        <td>{row.error || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button
                color="primary"
                disabled={
                  busy || !(count('matched') + count('matched_manually'))
                }
                onClick={() =>
                  run(async () => {
                    const confirmed = await chooseImportAction('Confirm matched records',
                      `Import confirmed matches into ${nameOf(user)} (${user.email}, ID ${userId})?`,
                      [{ value: 'confirm', label: 'Import matched records', color: 'primary' }]);
                    if (confirmed !== 'confirm') return;
                    let result;
                    do {
                      // Each request commits at most 50 rows; continue until the staged job is drained.
                      // eslint-disable-next-line no-await-in-loop
                      result = await executeDataImport(userId, jobId!);
                    } while (result.remaining > 0);
                    await loadJob(jobId!);
                    await refresh();
                  })
                }
              >
                Confirm and import matched records
              </Button>{' '}
              {count('failed') > 0 && (
                <Button
                  color="warning"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await executeDataImport(userId, jobId!, true);
                      await loadJob(jobId!);
                      await refresh();
                    })
                  }
                >
                  Retry failed records
                </Button>
              )}{' '}
              {(
                [
                  ['all', 'All results'],
                  ['imported', 'Imported'],
                  ['review', 'Needs review'],
                  ['unmatched', 'Unmatched and review'],
                  ['skipped', 'Skipped'],
                  ['failed', 'Failed'],
                  ['pending', 'Pending'],
                  ['unresolved', 'Unresolved and failed'],
                ] as const
              ).map(([status, label]) => (
                <Button
                  key={status}
                  color="secondary"
                  className="mr-2 mb-2"
                  disabled={busy}
                  onClick={() =>
                    run(async () =>
                      saveBlob(
                        await downloadImportReport(userId, jobId!, status),
                        `boxpressd-import-${jobId}-${status}.csv`
                      )
                    )
                  }
                >
                  Download {label.toLowerCase()}
                </Button>
              ))}
              <Button color="primary" className="mr-2 mb-2" disabled={busy}
                onClick={() => run(async () => saveBlob(
                  await downloadImportReport(userId, jobId!, 'reimport'),
                  `boxpressd-import-${jobId}-reimport.csv`
                ))}>
                Download reimport CSV
              </Button>
              {jobDetail.reconciliation.total_groups > 0 && (
                <p>
                  {jobDetail.reconciliation.reviewed_groups} of{' '}
                  {jobDetail.reconciliation.total_groups} cigar groups reviewed.
                </p>
              )}
              {groups.length > 0 && (
                <>
                  <h6 className="mt-4">
                    Review unmatched cigars ({groups.length} groups)
                  </h6>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Original brand</th>
                          <th>Cigar</th>
                          <th>Vitola</th>
                          <th>Suggested cigar ID</th>
                          <th>Suggested vitola ID</th>
                          <th>Cigar confidence</th>
                          <th>Vitola confidence</th>
                          <th>Review reason</th>
                          <th>Affected</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groups.map((group) => (
                          <tr key={group.group_key}>
                            <td>{group.brand}</td>
                            <td>{group.cigar_name}</td>
                            <td>{group.vitola}</td>
                            <td title={group.suggested_cigar_full_name || undefined}>
                              {group.suggested_cigar_id || '—'}
                            </td>
                            <td title={group.suggested_vitola_formatted_name || undefined}>
                              {group.suggested_vitola_id || '—'}
                            </td>
                            <td>
                              {group.confidence === null
                                ? '—'
                                : `${Math.round(group.confidence * 100)}%`}
                            </td>
                            <td>
                              {group.vitola_confidence == null
                                ? 'Unverified'
                                : `${Math.round(group.vitola_confidence * 100)}%`}
                            </td>
                            <td>{group.review_reasons?.join(', ') || '—'}</td>
                            <td>{group.affected_records}</td>
                            <td>{statusLabel(group.status)}</td>
                            <td>
                              <Button
                                size="sm"
                                onClick={() => openReviewMatch(group)}
                              >
                                Search / select
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            disabled={busy}
            onClick={closeImportModal}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={activeModal === 'import' && selectedGroup !== null}
        toggle={() => !busy && closeReviewMatch()}
        size="lg"
        scrollable
        zIndex={1070}
      >
        <ModalHeader toggle={() => !busy && closeReviewMatch()}>
          Search / select cigar match
        </ModalHeader>
        <ModalBody>
          {selectedGroup && (
            <>
              <p>
                <strong>Source:</strong> {reviewSearchQuery(selectedGroup)}{' '}
                {selectedGroup.vitola || ''} · {selectedGroup.affected_records} affected
                {selectedGroup.affected_records === 1 ? ' record' : ' records'}
              </p>
              <p className="text-muted">
                Suggested cigar ID:{' '}
                <span title={selectedGroup.suggested_cigar_full_name || undefined}>
                  {selectedGroup.suggested_cigar_id || '—'}
                </span>{' '}
                · Suggested vitola ID:{' '}
                <span title={selectedGroup.suggested_vitola_formatted_name || undefined}>
                  {selectedGroup.suggested_vitola_id || '—'}
                </span>
              </p>
              {error && <div className="alert alert-danger" role="alert">{error}</div>}
              <form onSubmit={(event) => { event.preventDefault();
                searchReviewCigars(query, selectedGroup.suggested_cigar_id); }}>
                <div className="input-group mb-3">
                  <input
                    className="form-control"
                    aria-label="Search cigars"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    autoFocus
                  />
                  <Button type="submit" disabled={matchSearching || query.trim().length < 2}>
                    Search cigars
                  </Button>
                </div>
              </form>
              {matchSearching && <p>Searching catalog...</p>}
              {!matchSearching && hasSearched && cigars.length === 0 &&
                <p>No catalog results. Try a shorter cigar or brand name.</p>}
              {cigars.map((cigar) => (
                <div key={cigar.cigar_id} className="border rounded p-2 mt-2">
                  {cigar.image_url && (
                    <img src={cigar.image_url} alt="" width={48} height={48}
                      style={{ objectFit: 'contain', marginRight: 8 }} />
                  )}
                  <strong>{cigar.brand} · {cigar.name}</strong>{' '}
                  · ID {cigar.cigar_id} · {cigar.wrapper || 'Wrapper unknown'} ·{' '}
                  {cigar.origin || 'Origin unknown'} · {cigar.vitola_options.length} vitolas{' '}
                  <Button size="sm" color="secondary" outline disabled={busy}
                    onClick={() => {
                      setReviewCigarId(reviewCigarId === cigar.cigar_id
                        ? null : cigar.cigar_id);
                      setReviewVitolaChoice(null);
                    }}>
                    {reviewCigarId === cigar.cigar_id ? 'Hide vitolas' : 'Inspect vitolas'}
                  </Button>
                  {reviewCigarId === cigar.cigar_id && (
                    <div className="mt-3 border-top pt-2">
                      <p className="mb-2">
                        <strong>Inspect this cigar’s vitolas, then select one</strong> for source{' '}
                        {selectedGroup.shape || selectedGroup.vitola || 'shape unspecified'}
                        {selectedGroup.size ? ` · ${selectedGroup.size}` : ''}.
                        Nothing is applied until you confirm the pair below.
                      </p>
                      <input className="form-control form-control-sm mb-2"
                        aria-label="Filter vitolas"
                        placeholder="Filter vitolas by name, size, or ID"
                        value={vitolaFilter}
                        onChange={(event) => setVitolaFilter(event.target.value)} />
                      <div className="d-flex flex-wrap" style={{ gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                        {[...cigar.vitola_options].sort((left, right) =>
                          Number(right.id === selectedGroup.suggested_vitola_id) -
                          Number(left.id === selectedGroup.suggested_vitola_id)
                        ).filter((vitola) =>
                          !vitolaFilter.trim() || [vitola.id, vitola.formatted_name,
                            vitola.shape, vitola.alias, vitola.dimen]
                            .join(' ').toLowerCase().includes(vitolaFilter.trim().toLowerCase())
                        ).map((vitola) => (
                          <Button key={vitola.id} size="sm"
                            color={reviewVitolaChoice === vitola.id ? 'primary' : 'secondary'}
                            outline={reviewVitolaChoice !== vitola.id}
                            title={vitola.formatted_name || undefined}
                            onClick={() => setReviewVitolaChoice(vitola.id)}>
                            #{vitola.id} · {vitola.formatted_name || 'Unnamed vitola'}
                            {vitola.id === selectedGroup.suggested_vitola_id ? ' · Suggested' : ''}
                          </Button>
                        ))}
                      </div>
                      {vitolaFilter && !cigar.vitola_options.some((vitola) =>
                        [vitola.id, vitola.formatted_name, vitola.shape,
                          vitola.alias, vitola.dimen].join(' ').toLowerCase()
                          .includes(vitolaFilter.trim().toLowerCase())) && (
                        <Button size="sm" color="link" onClick={() => setVitolaFilter('')}>
                          No vitolas match this filter. Show all vitolas.
                        </Button>
                      )}
                      <Button size="sm" className="mt-2"
                        color={reviewVitolaChoice === 'unlinked' ? 'warning' : 'secondary'}
                        outline={reviewVitolaChoice !== 'unlinked'}
                        onClick={() => setReviewVitolaChoice('unlinked')}>
                        Leave vitola unlinked
                      </Button>
                      {reviewVitolaChoice === 'unlinked' && (
                        <small className="d-block text-warning mt-1">
                          This imports the record with the selected cigar and no vitola ID.
                        </small>
                      )}
                      {reviewVitolaChoice !== null && (
                        <div className="alert alert-info mt-3 mb-2" role="status">
                          <strong>Proposed match:</strong> {cigar.brand} {cigar.name} · cigar ID{' '}
                          {cigar.cigar_id} · {reviewVitolaChoice === 'unlinked'
                            ? 'no vitola ID'
                            : `${cigar.vitola_options.find((item) =>
                              item.id === reviewVitolaChoice)?.formatted_name || 'Vitola'} · vitola ID ${reviewVitolaChoice}`}
                        </div>
                      )}
                      <div className="mt-3">
                        <Button size="sm" color="primary"
                          disabled={busy || reviewVitolaChoice === null}
                          onClick={() => run(async () => {
                            const choice = reviewVitolaChoice === 'unlinked'
                              ? { vitolaDecision: 'unlinked' as const, vitolaId: null }
                              : { vitolaDecision: 'selected' as const,
                                vitolaId: reviewVitolaChoice as number };
                            const matched = await applyImportMatch(
                              userId, jobId!, selectedGroup.group_key, cigar.cigar_id, choice
                            );
                            for (const affectedJobId of matched.affectedJobIds || []) {
                              let batch;
                              do {
                                // Reconciliation may span separate inventory and session jobs.
                                // eslint-disable-next-line no-await-in-loop
                                batch = await executeDataImport(userId, affectedJobId);
                              } while (batch.remaining > 0);
                            }
                            closeReviewMatch();
                            await loadJob(jobId!);
                            await refresh();
                          })}>
                          Confirm and apply this match to {selectedGroup.affected_records}{' '}
                          {selectedGroup.affected_records === 1 ? 'record' : 'records'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {selectedGroup && (
            <>
              <Button size="sm" color="secondary" onClick={() => navigate('/cigars/editor/new', {
                state: { importSource: { brand: selectedGroup.brand,
                  name: selectedGroup.cigar_name, vitola: selectedGroup.vitola } },
              })}>
                Create missing cigar
              </Button>
              <Button size="sm" color="link" disabled={busy}
                onClick={() => run(async () => {
                  await skipImportMatch(userId, jobId!, selectedGroup.group_key);
                  closeReviewMatch();
                  await loadJob(jobId!);
                  await refresh();
                })}>
                Skip / leave pending
              </Button>
            </>
          )}
          <Button color="secondary" disabled={busy} onClick={closeReviewMatch}>Close</Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={activeModal === 'export'}
        toggle={() => !busy && setActiveModal(null)}
      >
        <ModalHeader toggle={() => !busy && setActiveModal(null)}>
          Export data for {nameOf(user)}
        </ModalHeader>
        <ModalBody>
          <p className="text-muted">
            {user.email} · User ID {userId}
          </p>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <h6>Export data</h6>
          <p>
            Exports are generated for {nameOf(user)} and downloaded directly to
            this browser.
          </p>
          <label className="mr-3">
            <input
              type="checkbox"
              checked={exportInventory}
              onChange={(event) => setExportInventory(event.target.checked)}
            />{' '}
            Humidor inventory
          </label>{' '}
          <label>
            <input
              type="checkbox"
              checked={exportSessions}
              onChange={(event) => setExportSessions(event.target.checked)}
            />{' '}
            Smoke sessions
          </label>
          <br />
          <Button
            color="secondary"
            disabled={busy || (!exportInventory && !exportSessions)}
            onClick={() =>
              run(async () =>
                saveBlob(
                  await downloadUserData(
                    userId,
                    exportInventory,
                    exportSessions
                  ),
                  `boxpressd-user-${userId}-export.zip`
                )
              )
            }
          >
            Export selected data
          </Button>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            disabled={busy}
            onClick={() => setActiveModal(null)}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
